import { createContext, useContext, useState, useCallback, useMemo, ReactNode } from "react";

export type BannerVariant = "info" | "error" | "warning" | "success";

export interface BannerAlert {
  id: string;
  variant: BannerVariant;
  title: string;
  body?: string;
  /** When true, title and body render on the same line */
  inline?: boolean;
}

interface BannerAlertContextValue {
  banners: BannerAlert[];
  addBanner: (banner: Omit<BannerAlert, "id"> & { id?: string }) => string;
  removeBanner: (id: string) => void;
  clearBanners: () => void;
}

const BannerAlertContext = createContext<BannerAlertContextValue | null>(null);

export function BannerAlertProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [banners, setBanners] = useState<BannerAlert[]>([]);

  const addBanner = useCallback((banner: Omit<BannerAlert, "id"> & { id?: string }) => {
    const id = banner.id ?? crypto.randomUUID();
    setBanners((prev) => [...prev, { ...banner, id }]);
    return id;
  }, []);

  const removeBanner = useCallback((id: string) => {
    setBanners((prev) => prev.filter((b) => b.id !== id));
  }, []);

  const clearBanners = useCallback(() => setBanners([]), []);

  const value = useMemo(
    () => ({ banners, addBanner, removeBanner, clearBanners }),
    [banners, addBanner, removeBanner, clearBanners]
  );

  return (
    <BannerAlertContext.Provider value={value}>
      {children}
    </BannerAlertContext.Provider>
  );
}

export function useBannerAlert() {
  const ctx = useContext(BannerAlertContext);
  if (!ctx) throw new Error("useBannerAlert must be used within a BannerAlertProvider");
  return ctx;
}
